<script setup>
// Shared, presentational tools CRUD. API-agnostic: the parent owns fetching and
// persistence. Renders connected tool bindings + a catalog of available tools
// to add, and an inline "custom tool" form with cURL import. Emits intent
// events; the parent maps them to whichever backend it uses (PowerAgents
// binding API here; PowerAgents can adopt later — see BUILDER-04).
import { ref, reactive, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import VariableTokenInput from './VariableTokenInput.vue';
import ToolVariablesPanel from './ToolVariablesPanel.vue';
import { lerCurl, partirUrl } from 'dashboard/helper/curl/lerCurl';

const props = defineProps({
  // TOOLS-05: só o FORMULÁRIO, sem a barra e sem as duas listas. A seção do PowerAgents passou a
  // ter uma lista única própria e a abrir este formulário dentro de um diálogo — reaproveitando o
  // que já existia (importar cURL, dry-run, parâmetro do agente vs fixo, token de variável,
  // rascunho redigido) em vez de escrever um segundo formulário que faria metade disso.
  apenasFormulario: { type: Boolean, default: false },
  // Bindings in CONTRACTS §2 shape: { id, enabled, position, overrides, tool:{...} }
  bindings: { type: Array, default: () => [] },
  // Catalog tools not yet bound: { id, name, description, http_method, ... }
  availableTools: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  // Optional async (payload) => { status, body, resolved_url } to dry-run a tool
  // before saving. When absent (e.g. PowerAgents), the Test affordance is hidden.
  onTest: { type: Function, default: null },
  // Optional async (payload) => Promise that persists a custom tool. Resolves on
  // success, rejects with a display message on failure. When set, the form only
  // closes on success and surfaces the error inline (#4 — never lose the draft).
  onSubmitCustom: { type: Function, default: null },
  // Optional async (toolId, toolData) => Promise to edit an existing custom tool.
  onUpdateTool: { type: Function, default: null },
  // Insertable {{var}} tokens for the value fields: [{ name, kind:'system'|'declared' }].
  variables: { type: Array, default: () => [] },
  // FLOW-13 (SPEC S1, S2): os nomes dos segredos da conta, para a caixa de variáveis. A tool antiga
  // acha o `{{nome}}` no cofre antes do agente (R2). Só nomes.
  segredos: { type: Array, default: () => [] },
  // localStorage key to auto-persist the in-progress custom tool draft (#4).
  // Empty = no persistence (e.g. PowerAgents until it opts in).
  draftKey: { type: String, default: '' },
});

const emit = defineEmits([
  'bindExisting',
  'bindCustom',
  'unbind',
  'toggle',
  'deleteTool',
]);

const { t } = useI18n();

const METHODS = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'];

// Busca sobre nome e descrição. Com dez tools na conta a lista já obriga a rolar, e a
// pergunta de quem chega aqui é sempre "a tool X existe?" — não "o que existe?".
const busca = ref('');

function casa(tool) {
  const q = busca.value.trim().toLowerCase();
  if (!q) return true;
  return `${tool.name} ${tool.description || ''}`.toLowerCase().includes(q);
}

const bindingsFiltrados = computed(() =>
  props.bindings.filter(b => casa(b.tool || {}))
);
const catalogoFiltrado = computed(() => props.availableTools.filter(casa));

// Cor do método: GET lê, o resto escreve. Numa lista de tools é a distinção que importa
// antes de qualquer outra — ligar por engano uma tool que escreve é diferente de uma que lê.
function corDoMetodo(metodo) {
  return String(metodo).toUpperCase() === 'GET'
    ? 'bg-n-teal-3 text-n-teal-11'
    : 'bg-n-amber-3 text-n-amber-11';
}

const showForm = ref(false);

// TOOLS-05: cabecalhos, parametros e teste viraram secoes que abrem UMA POR VEZ. Abertas todas ao
// mesmo tempo a caixa rola, e foi para nao rolar que o formulario virou caixa. Fechadas por
// padrao: o basico (nome, o que faz, endereco) cabe na tela, e a contagem no cabecalho ja diz o
// que ha dentro sem precisar abrir.
const secaoAberta = ref('');
function alternarSecao(nome) {
  secaoAberta.value = secaoAberta.value === nome ? '' : nome;
}
const showCurl = ref(false);
const curlText = ref('');
const curlError = ref('');

const form = reactive({
  name: '',
  description: '',
  method: 'POST',
  url: '',
  headers: [{ key: 'Content-Type', value: 'application/json' }],
  params: [],
});
const formErrors = reactive({ name: '', url: '' });
const submitError = ref('');
const submitting = ref(false);
// null = creating a new tool; a tool id = editing that existing custom tool (#11).
const editingToolId = ref(null);
// O `auth` da era n8n da ferramenta em edição. O formulário não o edita nem o salva; o "Testar" o manda
// para o servidor montar o cabeçalho como o publish monta (Gate 4 da FLOW-13).
const authDaTool = ref(null);

const testing = ref(false);
const testResult = ref(null);
// O teste não tem lista para contar; o cabeçalho só acende quando há resultado para ver.
const contagemTeste = computed(() => (testResult.value ? 1 : 0));

// ── Draft persistence (#4) ─────────────────────────────────────────
// A custom tool is painstaking to build; a duplicate-name error or an
// accidental refresh must never wipe it. Mirror the form into localStorage
// on every change while it's open, restore on reopen, clear only on success.

// A raw secret (e.g. from a pasted cURL `Authorization: Bearer …`) must NOT persist to
// localStorage in cleartext. Redact secret-shaped header/param values before saving; `{{token}}`
// placeholders (the intended way to reference secrets) are kept — they resolve server-side.
const SECRET_SHAPE =
  /(bearer\s+\S+|sk-[a-z0-9-]{12,}|gsk_[a-z0-9]{12,}|[A-Za-z0-9+/_-]{40,})/i;
function redactValue(value) {
  if (typeof value !== 'string' || !value) return value;
  if (/\{\{[a-z0-9_]+\}\}/i.test(value)) return value; // keep placeholder tokens
  return SECRET_SHAPE.test(value) ? '' : value;
}
function safeDraft() {
  return {
    ...form,
    headers: form.headers.map(h => ({
      key: h.key,
      value: redactValue(h.value),
    })),
    params: form.params.map(p => ({ ...p, value: redactValue(p.value) })),
  };
}
function persistDraft() {
  if (!props.draftKey) return;
  try {
    window.localStorage.setItem(props.draftKey, JSON.stringify(safeDraft()));
  } catch (e) {
    // storage full/unavailable — persistence is best-effort, ignore
  }
}
function clearDraft() {
  if (!props.draftKey) return;
  try {
    window.localStorage.removeItem(props.draftKey);
  } catch (e) {
    // ignore
  }
}
function loadDraft() {
  if (!props.draftKey) return false;
  try {
    const raw = window.localStorage.getItem(props.draftKey);
    if (!raw) return false;
    const saved = JSON.parse(raw);
    form.name = saved.name || '';
    form.description = saved.description || '';
    form.method = saved.method || 'POST';
    form.url = saved.url || '';
    form.headers =
      Array.isArray(saved.headers) && saved.headers.length
        ? saved.headers
        : [{ key: 'Content-Type', value: 'application/json' }];
    form.params = Array.isArray(saved.params) ? saved.params : [];
    return true;
  } catch (e) {
    return false;
  }
}

watch(
  form,
  () => {
    // Only the create form has a recoverable draft; editing an existing tool must not
    // overwrite it (and its data already lives on the server).
    if (showForm.value && !editingToolId.value) persistDraft();
  },
  { deep: true }
);

// Prefill the form from an existing tool's server data → edit mode (#11).
function headersToRows(headers) {
  const entries = Object.entries(headers || {});
  return entries.length
    ? entries.map(([key, value]) => ({ key, value }))
    : [{ key: 'Content-Type', value: 'application/json' }];
}
const PARAM_TYPES = ['string', 'number', 'integer', 'boolean'];

function paramsSchemaToRows(schema) {
  const properties = schema?.properties || {};
  const required = schema?.required || [];
  return Object.entries(properties).map(([key, p]) => ({
    key,
    mode: p.default !== undefined ? 'static' : 'ai',
    type: PARAM_TYPES.includes(p.type) ? p.type : 'string',
    required: required.includes(key),
    description: p.description || '',
    example: '',
    value: p.default === undefined ? '' : String(p.default),
  }));
}
function openEditForm(tool) {
  editingToolId.value = tool.id;
  form.name = tool.name || '';
  form.description = tool.description || '';
  form.method = tool.http_method || 'POST';
  form.url = tool.url_template || '';
  form.headers = headersToRows(tool.headers);
  form.params = paramsSchemaToRows(tool.params_schema);
  authDaTool.value = tool.auth?.secret_key ? tool.auth : null;
  formErrors.name = '';
  formErrors.url = '';
  submitError.value = '';
  showForm.value = true;
}

// Quais {{variáveis}} de contexto esta tool usa, na URL, nos headers e no `auth`. Só as que a
// plataforma conhece: um {{token}} que não é variável nem segredo é erro de digitação, e
// quem recusa é o servidor, com o nome do token. O `secret_key` do `auth` com nome de variável
// é a variável (o R2 da FLOW-13), e pede o valor do teste como o `{{nome}}` do header.
const contextTokens = computed(() => {
  const nomes = new Set(props.variables.map(v => v.name));
  const chaveDoAuth = authDaTool.value?.secret_key;
  const alvo = [
    form.url,
    ...form.headers.map(h => h.value),
    chaveDoAuth ? `{{${chaveDoAuth}}}` : '',
  ].join(' ');
  const achados = [...String(alvo).matchAll(/\{\{([a-z0-9_]+)\}\}/g)]
    .map(m => m[1])
    .filter(nome => nomes.has(nome));
  return [...new Set(achados)];
});

// O rótulo do campo é o token como ele aparece na URL. Montado em JS porque as chaves duplas
// escritas no template seriam lidas como interpolação do próprio Vue.
function tokenLabel(nome) {
  return `{{${nome}}}`;
}

// O valor de cada variável de contexto PARA ESTE TESTE. Na execução quem preenche é o
// turno; aqui não há turno, e sem estes campos a tool que usa contexto viraria intestável —
// o servidor recusa o teste sem valor, que é o mesmo que a produção faz.
const contextSample = reactive({});

async function runTest() {
  if (!props.onTest) return;
  testing.value = true;
  testResult.value = null;
  try {
    const headers = form.headers.reduce((acc, h) => {
      if (h.key) acc[h.key] = h.value;
      return acc;
    }, {});
    const sample = form.params.reduce((acc, p) => {
      if (p.key) acc[p.key] = p.example || p.value || '';
      return acc;
    }, {});
    const context = contextTokens.value.reduce((acc, nome) => {
      acc[nome] = contextSample[nome] || '';
      return acc;
    }, {});
    // O `name` deixa a recusa do servidor nomear a ferramenta, como a do publish; o `auth` monta o
    // cabeçalho que o publish monta.
    testResult.value = await props.onTest({
      name: form.name.trim(),
      url_template: form.url,
      http_method: form.method,
      headers,
      params: sample,
      context,
      ...(authDaTool.value ? { auth: authDaTool.value } : {}),
    });
  } catch (e) {
    // A recusa do dry-run vem em 422 com o NOME do que faltou. Cair no `e.message` genérico
    // aqui devolveria "Request failed" para um erro que sabe dizer "conversation_id".
    const dados = e?.response?.data;
    testResult.value = {
      status: e?.response?.status || 0,
      body: dados?.detail || dados?.error || e?.message || 'Request failed',
    };
  } finally {
    testing.value = false;
  }
}

function resetForm() {
  form.name = '';
  form.description = '';
  form.method = 'POST';
  form.url = '';
  form.headers = [{ key: 'Content-Type', value: 'application/json' }];
  form.params = [];
  formErrors.name = '';
  formErrors.url = '';
  testResult.value = null;
  editingToolId.value = null;
  authDaTool.value = null;
}

function openForm() {
  editingToolId.value = null;
  authDaTool.value = null;
  formErrors.name = '';
  formErrors.url = '';
  submitError.value = '';
  // Restore an in-progress draft if one survived a previous error/refresh (#4).
  if (!loadDraft()) resetForm();
  showForm.value = true;
}
// Cancel/close keeps the draft on purpose (only success clears it), so a
// mis-click never destroys the work — reopening restores it.
function closeForm() {
  showForm.value = false;
}

// TOOLS-05: quem monta este componente com `apenasFormulario` precisa abrir o formulário de fora,
// porque a barra que o abria não está na tela.
defineExpose({ openForm, openEditForm, closeForm });

function addHeader() {
  form.headers.push({ key: '', value: '' });
}
function removeHeader(i) {
  form.headers.splice(i, 1);
}
function addParam() {
  form.params.push({
    key: '',
    mode: 'ai',
    type: 'string',
    required: true,
    description: '',
    example: '',
    value: '',
  });
}

function removeParam(i) {
  form.params.splice(i, 1);
}

// #3a: the Variables panel owns AI-value declaration but the parent owns the array.
function addParamRow(row) {
  form.params.push(row);
}
function removeParamRow(row) {
  const i = form.params.indexOf(row);
  if (i >= 0) form.params.splice(i, 1);
}

// #3b: raw-JSON view of the parameters (like n8n's "Using JSON"). The rows round-trip 1:1, so
// the compiler always reads form.params regardless of which view is active. Live-parses while
// editing; an invalid document just surfaces an error and leaves the last valid state intact.
const paramsJsonMode = ref(false);
const paramsJson = ref('');
const paramsJsonError = ref('');
// Reset the Fields/JSON toggle whenever the form (re)opens so a stale serialized JSON from a
// previous create/edit isn't shown out of sync with form.params.
watch(showForm, open => {
  if (open) {
    paramsJsonMode.value = false;
    paramsJson.value = '';
    paramsJsonError.value = '';
  }
});
function toggleParamsJson() {
  if (!paramsJsonMode.value) {
    paramsJson.value = JSON.stringify(form.params, null, 2);
    paramsJsonError.value = '';
  }
  paramsJsonMode.value = !paramsJsonMode.value;
}
function onParamsJsonInput(e) {
  paramsJson.value = e.target.value;
  try {
    const parsed = JSON.parse(paramsJson.value);
    if (!Array.isArray(parsed)) throw new Error('array');
    form.params = parsed.map(p => ({
      key: p.key || '',
      mode: p.mode === 'static' ? 'static' : 'ai',
      description: p.description || '',
      example: p.example || '',
      value: p.value || '',
    }));
    paramsJsonError.value = '';
  } catch (err) {
    paramsJsonError.value = t('POWER_AGENTS.TOOLS.FORM.PARAMS_JSON_ERROR');
  }
}

// Um valor de exemplo do cURL vira um parâmetro ESTÁTICO com aquele valor, e não um
// AI-value: o exemplo é um valor concreto, não uma instrução para o modelo preencher.
// Quem quiser que o modelo preencha troca o modo em um clique — o contrário (adivinhar
// que era para ser AI-value e perder o exemplo) não teria volta.
function paramFromCurl(key, value) {
  const numero = value !== '' && Number.isFinite(Number(value));
  const booleano = value === 'true' || value === 'false';
  let tipo = 'string';
  if (booleano) tipo = 'boolean';
  else if (numero)
    tipo = Number.isInteger(Number(value)) ? 'integer' : 'number';

  return {
    key,
    mode: 'static',
    type: tipo,
    required: false,
    description: '',
    example: String(value),
    value: String(value),
  };
}

// FLOW-11 (task 5.1, G18): a leitura do cURL é a do módulo compartilhado com o editor de flow
// (`helper/curl/lerCurl.js`), que lê como o bash (a barra no fim da linha, aspas dentro de aspas, o
// `$'…'` do Chrome). Este adaptador repete o que a tela de tools fazia com o texto: a query e o
// corpo viram parâmetros fixos, os headers são os do `-H`, e o nome sai do último pedaço do caminho.
// Os testes de caracterização (`specs/ToolsCrudCurl.spec.js`) são os de antes da troca.
function corpoDaFerramenta(dados) {
  if (!dados.length) return {};
  const bruto = dados
    .map(d => d.valor)
    .join('&')
    .trim();
  try {
    const json = JSON.parse(bruto);
    // Só objeto plano vira formulário: um array ou um valor solto não tem nome de campo,
    // e inventar um seria pior que não importar.
    return json && typeof json === 'object' && !Array.isArray(json) ? json : {};
  } catch {
    // Corpo em formato de formulário (a=1&b=2) também é um cURL comum.
    if (!bruto.includes('=')) return {};
    return Object.fromEntries(new URLSearchParams(bruto).entries());
  }
}

function curlParaFerramenta(curl) {
  const { base: url, query } = partirUrl(curl.url);
  let method = 'GET';
  if (curl.metodo) method = curl.metodo.toUpperCase();
  else if (curl.dados.length) method = 'POST';

  // PA-01 (T11.3): a query string era DESCARTADA. Quem cola um cURL de exemplo perdia
  // justamente os parâmetros que o exemplo demonstra, e tinha de redigitá-los à mão —
  // numa tela cuja razão de existir é não redigitar nada.
  const parametros = query.map(([chave, valor]) => paramFromCurl(chave, valor));

  // Só os do `-H`, como antes: o `-A` e o `-e` não viravam header nesta tela.
  const headers = curl.headers
    .filter(h => !h.de)
    .map(h => ({ key: h.nome, value: h.valor }));
  if (!headers.length)
    headers.push({ key: 'Content-Type', value: 'application/json' });

  // E o corpo também era ignorado. Um cURL de POST chegava sem parâmetro nenhum,
  // como se a tool não recebesse nada.
  Object.entries(corpoDaFerramenta(curl.dados)).forEach(([chave, valor]) => {
    if (!parametros.some(p => p.key === chave)) {
      parametros.push(paramFromCurl(chave, valor));
    }
  });

  return {
    // Sanitize to the model's required shape (^[a-z0-9_]+$) so the derived name doesn't block save.
    name:
      (url.split('/').pop() || '')
        .toLowerCase()
        .replace(/[^a-z0-9_]+/g, '_')
        .replace(/^_+|_+$/g, '') || 'imported_tool',
    description: '',
    url,
    method,
    headers,
    params: parametros,
  };
}

function parseCurl() {
  curlError.value = '';
  const lido = lerCurl(curlText.value);
  if (!lido.ok) {
    curlError.value = t('POWER_AGENTS.TOOLS.CURL_ERROR');
    return;
  }
  Object.assign(form, curlParaFerramenta(lido.curl));
  showCurl.value = false;
}

function buildHeadersObj() {
  return form.headers.reduce((acc, h) => {
    if (h.key.trim()) acc[h.key.trim()] = h.value;
    return acc;
  }, {});
}

// Build a JSONSchema-ish params_schema from the editor rows ($fromAI style).
// O valor estático vem do formulário como texto. Guardá-lo como texto num campo declarado
// como número faria o schema contradizer o próprio default.
function coerceDefault(value, type) {
  if (type === 'number' || type === 'integer') {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  }
  if (type === 'boolean') return value === true || value === 'true';
  return value || '';
}

// PA-01 (T11.2). Duas correções que mudam o que o runtime consegue validar:
//
// 1. `required` recebia TODAS as chaves, inclusive as estáticas. Não havia como declarar
//    um parâmetro opcional, e o schema afirmava uma obrigatoriedade que a tool não tinha.
// 2. `type` era sempre 'string'. Um id de pedido chegava como "42" e a validação não
//    distinguia 42 de "abc" — que é exatamente o que o novo runtime precisa barrar antes
//    de executar a tool.
//
// Parâmetro estático nunca é obrigatório: ele já vem preenchido, o modelo não o fornece.
function buildParamsSchema() {
  const rows = form.params.filter(p => p.key.trim());
  const properties = rows.reduce((acc, p) => {
    const key = p.key.trim();
    const type = PARAM_TYPES.includes(p.type) ? p.type : 'string';
    acc[key] = {
      type,
      description: p.mode === 'ai' ? p.description || '' : '',
    };
    if (p.mode === 'static') acc[key].default = coerceDefault(p.value, type);
    return acc;
  }, {});
  const required = rows
    .filter(p => p.mode === 'ai' && p.required !== false)
    .map(p => p.key.trim());
  return { type: 'object', properties, required };
}

async function submitCustom() {
  submitError.value = '';
  const trimmedName = form.name.trim();
  if (!trimmedName) {
    formErrors.name = t('POWER_AGENTS.TOOLS.FORM.NAME_REQUIRED');
  } else if (!/^[a-z0-9_]+$/.test(trimmedName)) {
    formErrors.name = t('POWER_AGENTS.TOOLS.FORM.NAME_INVALID');
  } else {
    formErrors.name = '';
  }
  formErrors.url = form.url.trim()
    ? ''
    : t('POWER_AGENTS.TOOLS.FORM.URL_REQUIRED');
  if (formErrors.name || formErrors.url) return;

  const toolData = {
    name: form.name.trim(),
    description: form.description.trim(),
    http_method: form.method,
    url_template: form.url.trim(),
    headers: buildHeadersObj(),
    params_schema: buildParamsSchema(),
  };

  // Edit mode (#11): update the existing custom tool; no draft/binding involved.
  if (editingToolId.value && props.onUpdateTool) {
    submitting.value = true;
    try {
      await props.onUpdateTool(editingToolId.value, toolData);
      closeForm();
      resetForm();
    } catch (e) {
      submitError.value = e?.message || t('POWER_AGENTS.TOOLS.ERROR');
    } finally {
      submitting.value = false;
    }
    return;
  }

  const payload = { tool: toolData };

  // Legacy fire-and-forget path (parents that only wired the emit).
  if (!props.onSubmitCustom) {
    emit('bindCustom', payload);
    clearDraft();
    closeForm();
    resetForm();
    return;
  }

  // #4: await the persist; keep the form (and its draft) intact on failure so a
  // duplicate name or backend error never wipes the work — show it inline.
  submitting.value = true;
  try {
    await props.onSubmitCustom(payload);
    clearDraft();
    closeForm();
    resetForm();
  } catch (e) {
    submitError.value = e?.message || t('POWER_AGENTS.TOOLS.ERROR');
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <div class="flex flex-col gap-5">
    <!-- Barra: busca + criar. As duas ações que trazem alguém a esta tela. -->
    <div v-if="!apenasFormulario" class="flex flex-wrap items-center gap-2">
      <Input
        v-model="busca"
        :placeholder="t('POWER_AGENTS.TOOLS.SEARCH')"
        class="group flex-1 min-w-48 [&>input]:ltr:!pl-8 [&>input]:rtl:!pr-8"
        size="sm"
        type="search"
      >
        <template #prefix>
          <Icon
            icon="i-lucide-search"
            class="absolute top-1/2 -translate-y-1/2 text-n-slate-11 group-focus-within:text-n-brand size-3.5 ltr:left-2.5 rtl:right-2.5"
          />
        </template>
      </Input>
      <Button
        :label="t('POWER_AGENTS.TOOLS.IMPORT_CURL')"
        icon="i-lucide-terminal"
        slate
        faded
        sm
        @click="showCurl = !showCurl"
      />
      <Button
        :label="t('POWER_AGENTS.TOOLS.NEW_CUSTOM')"
        icon="i-lucide-plus"
        sm
        @click="openForm"
      />
    </div>

    <!-- Tools ligadas a este agente -->
    <div v-if="!apenasFormulario" class="flex flex-col gap-2">
      <p class="text-xs font-medium tracking-wide uppercase text-n-slate-11">
        {{ t('POWER_AGENTS.TOOLS.CONNECTED') }}
      </p>

      <div v-if="loading" class="py-4 text-xs text-center text-n-slate-10">
        {{ t('POWER_AGENTS.TOOLS.LOADING') }}
      </div>
      <div
        v-else-if="!bindings.length"
        class="flex flex-col items-center gap-1 px-4 py-6 text-center border border-dashed rounded-xl border-n-weak"
      >
        <i class="text-lg i-lucide-wrench text-n-slate-9" />
        <p class="text-xs text-n-slate-10">
          {{ t('POWER_AGENTS.TOOLS.EMPTY') }}
        </p>
      </div>
      <p
        v-else-if="!bindingsFiltrados.length"
        class="px-1 text-xs text-n-slate-10"
      >
        {{ t('POWER_AGENTS.TOOLS.NO_MATCH') }}
      </p>

      <div v-else class="grid gap-2 sm:grid-cols-2">
        <div
          v-for="b in bindingsFiltrados"
          :key="b.id"
          class="flex flex-col gap-2 p-3 transition-colors border rounded-xl border-n-weak bg-n-alpha-1 hover:border-n-slate-6"
          :class="{ 'opacity-60': !b.enabled }"
        >
          <div class="flex items-center gap-2">
            <span
              class="flex items-center justify-center rounded-lg size-8 shrink-0 bg-n-brand/10"
            >
              <i class="text-sm i-lucide-zap text-n-brand" />
            </span>
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-1.5">
                <span class="text-sm font-medium truncate text-n-slate-12">
                  {{ b.tool.name }}
                </span>
                <span
                  v-if="b.tool.builtin"
                  class="text-xxs px-1.5 py-0.5 rounded bg-n-brand/10 text-n-brand shrink-0"
                >
                  {{ t('POWER_AGENTS.TOOLS.BUILTIN') }}
                </span>
              </div>
              <span
                class="text-xxs px-1.5 py-0.5 rounded font-mono"
                :class="corDoMetodo(b.tool.http_method)"
              >
                {{ b.tool.http_method }}
              </span>
            </div>
            <Switch
              :model-value="b.enabled"
              class="shrink-0"
              @update:model-value="$emit('toggle', b.id, $event)"
            />
          </div>

          <p class="text-xs line-clamp-2 text-n-slate-10">
            {{ b.tool.description }}
          </p>

          <div
            class="flex items-center gap-1 pt-1 mt-auto border-t border-n-weak"
          >
            <Button
              v-if="!b.tool.builtin"
              icon="i-lucide-pencil"
              :label="t('POWER_AGENTS.TOOLS.EDIT')"
              slate
              link
              xs
              @click="openEditForm(b.tool)"
            />
            <Button
              icon="i-lucide-unplug"
              :label="t('POWER_AGENTS.TOOLS.UNBIND')"
              slate
              link
              xs
              class="ml-auto"
              @click="$emit('unbind', b.id)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Catálogo da conta -->
    <div v-if="!apenasFormulario" class="flex flex-col gap-2">
      <p class="text-xs font-medium tracking-wide uppercase text-n-slate-11">
        {{ t('POWER_AGENTS.TOOLS.AVAILABLE') }}
      </p>
      <div
        v-if="!availableTools.length"
        class="px-4 py-4 text-xs text-center border border-dashed rounded-xl border-n-weak text-n-slate-10"
      >
        {{ t('POWER_AGENTS.TOOLS.CATALOG_EMPTY') }}
      </div>
      <p
        v-else-if="!catalogoFiltrado.length"
        class="px-1 text-xs text-n-slate-10"
      >
        {{ t('POWER_AGENTS.TOOLS.NO_MATCH') }}
      </p>
      <div v-else class="flex flex-col gap-1.5">
        <div
          v-for="tool in catalogoFiltrado"
          :key="tool.id"
          class="flex items-center gap-3 px-3 py-2.5 border rounded-xl border-n-weak hover:border-n-slate-6"
        >
          <span
            class="text-xxs px-1.5 py-0.5 rounded font-mono shrink-0"
            :class="corDoMetodo(tool.http_method)"
          >
            {{ tool.http_method }}
          </span>
          <div class="flex-1 min-w-0">
            <span class="text-sm font-medium text-n-slate-12">
              {{ tool.name }}
            </span>
            <p class="text-xs truncate text-n-slate-10">
              {{ tool.description }}
            </p>
          </div>
          <Button
            v-if="!tool.builtin"
            icon="i-lucide-pencil"
            :title="t('POWER_AGENTS.TOOLS.EDIT')"
            slate
            link
            xs
            @click="openEditForm(tool)"
          />
          <Button
            v-if="!tool.builtin"
            icon="i-lucide-trash-2"
            :title="t('POWER_AGENTS.TOOLS.DELETE')"
            ruby
            link
            xs
            @click="$emit('deleteTool', tool.id)"
          />
          <Button
            :label="t('POWER_AGENTS.TOOLS.BIND')"
            icon="i-lucide-plus"
            slate
            faded
            xs
            class="shrink-0"
            @click="$emit('bindExisting', tool.id)"
          />
        </div>
      </div>
    </div>

    <!-- cURL import -->
    <div
      v-if="showCurl"
      class="p-4 border rounded-lg border-n-brand/40 bg-n-alpha-1"
    >
      <h4 class="mb-1 text-sm font-semibold text-n-slate-12">
        {{ t('POWER_AGENTS.TOOLS.CURL_TITLE') }}
      </h4>
      <p class="mb-3 text-xs text-n-slate-10">
        {{ t('POWER_AGENTS.TOOLS.CURL_DESC') }}
      </p>
      <textarea
        v-model="curlText"
        rows="5"
        class="w-full px-3 py-2 font-mono text-xs border rounded-md outline-none border-n-weak bg-n-background"
        spellcheck="false"
      />
      <p v-if="curlError" class="mt-1.5 text-xs text-n-ruby-10">
        {{ curlError }}
      </p>
      <div class="flex justify-end mt-3">
        <Button
          :label="t('POWER_AGENTS.TOOLS.CURL_PARSE')"
          sm
          @click="parseCurl"
        />
      </div>
    </div>

    <!-- Custom tool form -->
    <div
      v-if="showForm"
      class="flex flex-col gap-4 p-5 border rounded-lg border-n-weak"
    >
      <div class="grid grid-cols-3 gap-3">
        <div class="col-span-2">
          <label class="block mb-1 text-xs font-medium text-n-slate-11">
            {{ t('POWER_AGENTS.TOOLS.FORM.NAME') }}
          </label>
          <input
            v-model="form.name"
            class="w-full px-3 py-2 text-sm border rounded-md outline-none bg-n-background"
            :class="formErrors.name ? 'border-n-ruby-8' : 'border-n-weak'"
          />
          <p v-if="formErrors.name" class="mt-1 text-xs text-n-ruby-10">
            {{ formErrors.name }}
          </p>
        </div>
        <div>
          <label class="block mb-1 text-xs font-medium text-n-slate-11">
            {{ t('POWER_AGENTS.TOOLS.FORM.METHOD') }}
          </label>
          <select
            v-model="form.method"
            class="w-full px-3 py-2 text-sm border rounded-md outline-none border-n-weak bg-n-background"
          >
            <option v-for="m in METHODS" :key="m" :value="m">{{ m }}</option>
          </select>
        </div>
      </div>

      <div>
        <label class="block mb-1 text-xs font-medium text-n-slate-11">
          {{ t('POWER_AGENTS.TOOLS.FORM.DESCRIPTION') }}
        </label>
        <textarea
          v-model="form.description"
          rows="2"
          :placeholder="t('POWER_AGENTS.TOOLS.FORM.DESC_PLACEHOLDER')"
          class="w-full px-3 py-2 text-sm border rounded-md outline-none resize-none border-n-weak bg-n-background"
        />
      </div>

      <!-- Variables toolbox (#3a): declare AI values + drag/click context vars into any field. -->
      <ToolVariablesPanel
        :variables="variables"
        :segredos="segredos"
        :params="form.params"
        @add-param="addParamRow"
        @remove-param="removeParamRow"
      />

      <div>
        <label class="block mb-1 text-xs font-medium text-n-slate-11">
          {{ t('POWER_AGENTS.TOOLS.FORM.URL') }}
        </label>
        <VariableTokenInput
          v-model="form.url"
          :variables="variables"
          :ai-params="form.params"
          :placeholder="t('POWER_AGENTS.TOOLS.FORM.URL_PLACEHOLDER')"
          :input-class="`w-full px-3 py-2 font-mono text-sm border rounded-md outline-none bg-n-background ${formErrors.url ? 'border-n-ruby-8' : 'border-n-weak'}`"
        />
        <p v-if="formErrors.url" class="mt-1 text-xs text-n-ruby-10">
          {{ formErrors.url }}
        </p>
        <p class="mt-1 text-xs text-n-slate-10">
          {{ t('POWER_AGENTS.TOOLS.FORM.URL_HINT') }}
        </p>
      </div>

      <div class="border rounded-lg border-n-weak">
        <button
          type="button"
          class="flex items-center w-full gap-2 px-3 py-2 text-xs font-medium text-n-slate-11"
          @click="alternarSecao('headers')"
        >
          <i
            :class="
              secaoAberta === 'headers'
                ? 'i-lucide-chevron-down'
                : 'i-lucide-chevron-right'
            "
          />
          {{ t('POWER_AGENTS.TOOLS.SECTION_HEADERS') }}
          <span
            v-if="form.headers.length"
            class="ltr:ml-auto rtl:mr-auto text-n-slate-10"
          >
            {{ form.headers.length }}
          </span>
        </button>
        <div v-if="secaoAberta === 'headers'" class="px-3 pb-3">
          <div class="flex items-center justify-between mb-1.5">
            <label class="text-xs font-medium text-n-slate-11">
              {{ t('POWER_AGENTS.TOOLS.FORM.HEADERS') }}
            </label>
            <button class="text-xs font-medium text-n-brand" @click="addHeader">
              {{ t('POWER_AGENTS.TOOLS.FORM.ADD') }}
            </button>
          </div>
          <div class="flex flex-col gap-1.5">
            <div v-for="(h, i) in form.headers" :key="i" class="flex gap-1.5">
              <input
                v-model="h.key"
                :placeholder="t('POWER_AGENTS.TOOLS.FORM.HEADER_KEY')"
                class="flex-1 px-2.5 py-1.5 text-xs font-mono border rounded outline-none border-n-weak bg-n-background"
              />
              <VariableTokenInput
                v-model="h.value"
                :variables="variables"
                :ai-params="form.params"
                wrapper-class="flex-1"
                :placeholder="t('POWER_AGENTS.TOOLS.FORM.HEADER_VALUE')"
                input-class="w-full px-2.5 py-1.5 text-xs font-mono border rounded outline-none border-n-weak bg-n-background"
              />
              <button
                class="px-1 text-n-slate-9 hover:text-n-ruby-10"
                @click="removeHeader(i)"
              >
                <i class="i-lucide-x" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="border rounded-lg border-n-weak">
        <button
          type="button"
          class="flex items-center w-full gap-2 px-3 py-2 text-xs font-medium text-n-slate-11"
          @click="alternarSecao('params')"
        >
          <i
            :class="
              secaoAberta === 'params'
                ? 'i-lucide-chevron-down'
                : 'i-lucide-chevron-right'
            "
          />
          {{ t('POWER_AGENTS.TOOLS.SECTION_PARAMS') }}
          <span
            v-if="form.params.length"
            class="ltr:ml-auto rtl:mr-auto text-n-slate-10"
          >
            {{ form.params.length }}
          </span>
        </button>
        <div v-if="secaoAberta === 'params'" class="px-3 pb-3">
          <div class="flex items-center justify-between mb-1.5">
            <label class="text-xs font-medium text-n-slate-11">
              {{ t('POWER_AGENTS.TOOLS.FORM.PARAMS') }}
            </label>
            <div class="flex items-center gap-3">
              <!-- Fields ⇄ JSON toggle (#3b), n8n-style raw editing of the parameters. -->
              <div
                class="flex text-xs rounded-md overflow-hidden border border-n-weak"
              >
                <button
                  class="px-2 py-0.5"
                  :class="
                    !paramsJsonMode
                      ? 'bg-n-brand text-white'
                      : 'bg-n-alpha-1 text-n-slate-10'
                  "
                  @click="paramsJsonMode && toggleParamsJson()"
                >
                  {{ t('POWER_AGENTS.TOOLS.FORM.PARAMS_FIELDS') }}
                </button>
                <button
                  class="px-2 py-0.5"
                  :class="
                    paramsJsonMode
                      ? 'bg-n-brand text-white'
                      : 'bg-n-alpha-1 text-n-slate-10'
                  "
                  @click="!paramsJsonMode && toggleParamsJson()"
                >
                  {{ t('POWER_AGENTS.TOOLS.FORM.PARAMS_JSON') }}
                </button>
              </div>
              <button
                v-if="!paramsJsonMode"
                class="text-xs font-medium text-n-brand"
                @click="addParam"
              >
                {{ t('POWER_AGENTS.TOOLS.FORM.ADD') }}
              </button>
            </div>
          </div>

          <!-- Raw JSON view -->
          <div v-if="paramsJsonMode" class="flex flex-col gap-1">
            <textarea
              :value="paramsJson"
              rows="8"
              spellcheck="false"
              class="w-full px-3 py-2 font-mono text-xs border rounded-md outline-none resize-y border-n-weak bg-n-background"
              @input="onParamsJsonInput"
            />
            <p v-if="paramsJsonError" class="text-xs text-n-ruby-10">
              {{ paramsJsonError }}
            </p>
          </div>

          <div v-else class="flex flex-col gap-2">
            <div
              v-for="(p, i) in form.params"
              :key="i"
              class="flex flex-col gap-1.5 p-3 border rounded-md border-n-weak"
            >
              <div class="flex items-center gap-2">
                <input
                  v-model="p.key"
                  :placeholder="t('POWER_AGENTS.TOOLS.FORM.PARAM_NAME')"
                  class="flex-1 px-2.5 py-1.5 text-xs font-mono border rounded outline-none border-n-weak bg-n-background"
                />
                <select
                  v-model="p.mode"
                  class="w-16 px-2 py-1.5 text-xs border rounded outline-none border-n-weak bg-n-background"
                >
                  <option value="ai">
                    {{ t('POWER_AGENTS.TOOLS.FORM.PARAM_AI') }}
                  </option>
                  <option value="static">
                    {{ t('POWER_AGENTS.TOOLS.FORM.PARAM_FIXED') }}
                  </option>
                </select>
                <button
                  class="px-1 text-n-slate-9 hover:text-n-ruby-10"
                  @click="removeParam(i)"
                >
                  <i class="i-lucide-x" />
                </button>
              </div>
              <div v-if="p.mode === 'ai'" class="flex flex-col gap-1.5">
                <input
                  v-model="p.description"
                  :placeholder="t('POWER_AGENTS.TOOLS.FORM.PARAM_DESC')"
                  class="px-2.5 py-1.5 text-xs border rounded outline-none border-n-weak bg-n-background"
                />
                <input
                  v-model="p.example"
                  :placeholder="t('POWER_AGENTS.TOOLS.FORM.PARAM_EXAMPLE')"
                  class="px-2.5 py-1.5 text-xs font-mono border rounded outline-none border-n-weak bg-n-background"
                />
              </div>
              <VariableTokenInput
                v-else
                v-model="p.value"
                :variables="variables"
                :ai-params="form.params"
                :placeholder="t('POWER_AGENTS.TOOLS.FORM.PARAM_VALUE')"
                input-class="w-full px-2.5 py-1.5 text-xs font-mono border rounded outline-none border-n-weak bg-n-background"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Test tool before saving (#3b): runs the request server-side with the
           example values above; secrets in headers are resolved server-side. -->
      <div class="border rounded-lg border-n-weak">
        <button
          type="button"
          class="flex items-center w-full gap-2 px-3 py-2 text-xs font-medium text-n-slate-11"
          @click="alternarSecao('test')"
        >
          <i
            :class="
              secaoAberta === 'test'
                ? 'i-lucide-chevron-down'
                : 'i-lucide-chevron-right'
            "
          />
          {{ t('POWER_AGENTS.TOOLS.SECTION_TEST') }}
          <span
            v-if="contagemTeste"
            class="ltr:ml-auto rtl:mr-auto text-n-slate-10"
          >
            {{ contagemTeste }}
          </span>
        </button>
        <div v-if="secaoAberta === 'test'" class="px-3 pb-3">
          <div class="flex items-center justify-between">
            <span class="text-xs font-medium text-n-slate-11">
              {{ t('POWER_AGENTS.TOOLS.FORM.TEST_TITLE') }}
            </span>
            <Button
              :label="t('POWER_AGENTS.TOOLS.FORM.TEST')"
              :is-loading="testing"
              :disabled="!form.url || testing"
              icon="i-lucide-play"
              faded
              slate
              sm
              @click="runTest"
            />
          </div>
          <!-- Valores de contexto para o teste. Na execução quem os preenche é o turno; aqui
             quem preenche é quem testa, e o que ficar vazio faz o teste parar — igual à
             produção, que recusa montar a chamada sem eles. -->
          <div v-if="contextTokens.length" class="mt-2">
            <p class="mb-1.5 text-xs text-n-slate-11">
              {{ t('POWER_AGENTS.TOOLS.FORM.TEST_CONTEXT_HINT') }}
            </p>
            <div class="grid grid-cols-2 gap-2">
              <label
                v-for="nome in contextTokens"
                :key="nome"
                class="flex flex-col gap-1"
              >
                <code class="text-xs font-mono text-n-slate-11">
                  {{ tokenLabel(nome) }}
                </code>
                <Input
                  v-model="contextSample[nome]"
                  custom-input-class="text-xs"
                />
              </label>
            </div>
          </div>
          <div
            v-if="testResult"
            class="mt-2 rounded-md border overflow-hidden"
            :class="
              testResult.status >= 200 && testResult.status < 300
                ? 'border-n-teal-6'
                : 'border-n-ruby-6'
            "
          >
            <div
              class="flex items-center gap-2 px-3 py-1.5 text-xs font-mono"
              :class="
                testResult.status >= 200 && testResult.status < 300
                  ? 'bg-n-teal-3 text-n-teal-11'
                  : 'bg-n-ruby-3 text-n-ruby-11'
              "
            >
              <span>{{ `HTTP ${testResult.status}` }}</span>
              <span
                v-if="testResult.resolved_url"
                class="truncate text-n-slate-11"
              >
                {{ testResult.method }} {{ testResult.resolved_url }}
              </span>
            </div>
            <pre
              class="px-3 py-2 text-xs font-mono whitespace-pre-wrap break-all max-h-48 overflow-y-auto text-n-slate-11 bg-n-background"
              >{{ testResult.body }}</pre>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-2 pt-3 border-t border-n-weak">
        <div
          v-if="submitError"
          class="flex items-start gap-1.5 px-3 py-2 text-xs rounded-md bg-n-ruby-3 text-n-ruby-11"
        >
          <i class="mt-px shrink-0 i-lucide-circle-alert" />
          <span>{{ submitError }}</span>
        </div>
        <div class="flex items-center justify-between gap-2">
          <span
            v-if="draftKey && !editingToolId"
            class="flex items-center gap-1 text-xs text-n-slate-9"
          >
            <i class="i-lucide-save" />
            {{ t('POWER_AGENTS.TOOLS.FORM.DRAFT_SAVED') }}
          </span>
          <span v-else />
          <div class="flex gap-2">
            <Button
              :label="t('POWER_AGENTS.TOOLS.FORM.CANCEL')"
              slate
              sm
              @click="closeForm"
            />
            <Button
              :label="
                editingToolId
                  ? t('POWER_AGENTS.TOOLS.FORM.SAVE_EDIT')
                  : t('POWER_AGENTS.TOOLS.FORM.SAVE')
              "
              :is-loading="submitting"
              :disabled="submitting"
              sm
              @click="submitCustom"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
